import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, featuredVideos, latestUpdates } from '@/mocks/home';

interface VideoItem {
  id: number;
  title: string;
  category: string;
  categorySlug?: string;
  cover: string;
  description?: string;
  rating: number;
  duration?: string;
  episodes?: number;
  isHot: boolean;
  isNew: boolean;
  views: number;
  updateEpisode?: string;
  updateTime?: string;
}

const allVideos: VideoItem[] = [
  ...featuredVideos,
  ...latestUpdates.map((v) => ({
    id: v.id,
    title: v.title,
    category: v.category,
    cover: v.cover,
    rating: 8.5,
    duration: v.updateEpisode === 'HD高清' ? '128分钟' : '45分钟/集',
    isHot: false,
    isNew: false,
    views: Math.floor(Math.random() * 5000000) + 1000000,
    description: '精彩内容，不容错过。高清画质，流畅播放，为您带来最佳的观影体验。',
    updateTime: v.updateTime,
  })),
];

const sortOptions = ['最新', '最热', '评分最高', '最多播放'];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedSort, setSelectedSort] = useState('最新');

  const category = categories.find((c) => c.slug === slug) || categories[0];

  const filteredVideos = allVideos.filter((v) => {
    if (v.categorySlug) return v.categorySlug === slug;
    const cat = categories.find((c) => c.name === v.category);
    return cat?.slug === slug;
  });

  const sortedVideos = [...filteredVideos].sort((a, b) => {
    if (selectedSort === '最热') return b.views - a.views;
    if (selectedSort === '评分最高') return b.rating - a.rating;
    return 0;
  });

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${category.name} - 玫瑰视频`,
        url: `https://meiguishipin.com/category/${slug}`,
        description: `玫瑰视频${category.name}分类，共${category.count}部高清影视作品免费在线观看。`,
        isPartOf: { '@type': 'WebSite', name: '玫瑰视频' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://meiguishipin.com' },
          { '@type': 'ListItem', position: 2, name: category.name, item: `https://meiguishipin.com/category/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <main>
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={[{ label: category.name }]} />
        </div>

        <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100">
                <i className={`${category.icon} text-lg md:text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500">共 {category.count.toLocaleString()} 部作品</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {sortOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setSelectedSort(option)}
                  className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    selectedSort === option
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            {sortedVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {sortedVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.cover}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        {video.isHot && (
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary-500 text-white whitespace-nowrap">热播</span>
                        )}
                        {video.isNew && (
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-accent-500 text-white whitespace-nowrap">新上</span>
                        )}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-white">
                          <i className="ri-play-fill text-lg"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {video.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                          {video.rating}
                        </span>
                        <span className="whitespace-nowrap">{video.views.toLocaleString()}次播放</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}